import { queryOptions, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import type { Canal, CanalEntrada, ContagemConversas, ContextoConversa, ConversaResumo, Feriado, Mensagem, RespostaRapida, SessaoOpenwa, StatusConversa } from "@horofacial/shared";
import { api, ErroRequisicao } from "./api";

export type FiltroConversa = "minhas" | "nao_atribuidas" | "aguardando" | "abertas" | "resolvidas" | "todas";

export const consultaCanais = queryOptions({ queryKey: ["whatsapp", "canais"], queryFn: () => api<Canal[]>("/whatsapp/canais") });

/** Números ativos sem conexão: mensagens de pacientes podem não chegar. Confere a cada minuto. */
export function useCanaisCaidos(habilitado: boolean) {
  const r = useQuery({ ...consultaCanais, enabled: habilitado, refetchInterval: 60_000 });
  return habilitado ? (r.data ?? []).filter((c) => c.ativo && c.status !== "conectado") : [];
}
export const consultaFeriados = queryOptions({ queryKey: ["whatsapp", "feriados"], queryFn: () => api<Feriado[]>("/whatsapp/feriados") });
export const consultaRespostas = queryOptions({ queryKey: ["conversas", "respostas"], queryFn: () => api<RespostaRapida[]>("/conversas-respostas") });
export const consultaAtendentes = queryOptions({ queryKey: ["conversas", "atendentes"], queryFn: () => api<{ id: string; nome: string }[]>("/conversas/atendentes") });

// Sem WebSocket: a lista e a conversa aberta se atualizam por consulta periódica.
export const consultaConversas = (filtro: FiltroConversa, busca: string) =>
  queryOptions({
    queryKey: ["conversas", "lista", filtro, busca],
    queryFn: () => api<ConversaResumo[]>(`/conversas?filtro=${filtro}${busca ? `&busca=${encodeURIComponent(busca)}` : ""}`),
    refetchInterval: 5_000,
  });
export const consultaContagemConversas = queryOptions({ queryKey: ["conversas", "contagem"], queryFn: () => api<ContagemConversas>("/conversas/contagem"), refetchInterval: 5_000 });
export const consultaConversa = (id: string) =>
  queryOptions({ queryKey: ["conversas", "uma", id], queryFn: () => api<ConversaResumo>(`/conversas/${id}`), refetchInterval: 5_000 });
export const consultaMensagens = (id: string) =>
  queryOptions({ queryKey: ["conversas", "mensagens", id], queryFn: () => api<Mensagem[]>(`/conversas/${id}/mensagens`), refetchInterval: 4_000 });
export const consultaContexto = (id: string) =>
  queryOptions({ queryKey: ["conversas", "contexto", id], queryFn: () => api<ContextoConversa>(`/conversas/${id}/contexto`) });

export const urlArquivoMensagem = (id: string) => `/api/conversas/mensagens/${id}/arquivo`;

function useAlterar<E, S>(fn: (e: E) => Promise<S>, chave: string) {
  const cliente = useQueryClient();
  return useMutation({ mutationFn: fn, onSuccess: () => cliente.invalidateQueries({ queryKey: [chave] }) });
}

export const useSalvarCanal = () =>
  useAlterar(
    ({ id, dados }: { id?: string; dados: CanalEntrada }) =>
      api<Canal & { webhookToken?: string }>(id ? `/whatsapp/canais/${id}` : "/whatsapp/canais", { method: id ? "PUT" : "POST", json: dados }),
    "whatsapp",
  );
export const useNovoToken = () => useAlterar((id: string) => api<{ webhookToken: string }>(`/whatsapp/canais/${id}/token`, { method: "POST" }), "whatsapp");
export const useAtualizarStatus = () => useAlterar((id: string) => api<Canal>(`/whatsapp/canais/${id}/status`, { method: "POST" }), "whatsapp");
export const useConectarCanal = () => useAlterar((id: string) => api<Canal>(`/whatsapp/canais/${id}/conectar`, { method: "POST" }), "whatsapp");
export const useDesconectarCanal = () => useAlterar((id: string) => api<Canal>(`/whatsapp/canais/${id}/desconectar`, { method: "POST" }), "whatsapp");

/** Endereço e chave do OpenWA: a chave digitada ou, sem ela, a já gravada no canal. */
type AcessoOpenwa = { urlConector: string; chaveApi?: string; canalId?: string };
export const useSessoesOpenwa = () => useMutation({ mutationFn: (a: AcessoOpenwa) => api<SessaoOpenwa[]>("/whatsapp/openwa/sessoes", { method: "POST", json: a }) });
export const useNovaSessaoOpenwa = () => useMutation({ mutationFn: (a: AcessoOpenwa & { nome: string }) => api<SessaoOpenwa>("/whatsapp/openwa/sessoes/nova", { method: "POST", json: a }) });
export const useNovoFeriado = () => useAlterar((f: { data: string; nome: string }) => api<Feriado>("/whatsapp/feriados", { method: "POST", json: f }), "whatsapp");
export const useFeriadosNacionais = () =>
  useAlterar((ano: number) => api<{ incluidos: number }>("/whatsapp/feriados/nacionais", { method: "POST", json: { ano } }), "whatsapp");
export const useRemoverFeriado = () => useAlterar((id: string) => api(`/whatsapp/feriados/${id}`, { method: "DELETE" }), "whatsapp");

export const useEnviarTexto = () =>
  useAlterar(({ id, texto, nota }: { id: string; texto: string; nota: boolean }) => api(`/conversas/${id}/mensagens`, { method: "POST", json: { texto, nota } }), "conversas");

/** Envia arquivo (ou áudio gravado) por multipart; legenda opcional. */
export const useEnviarAnexo = () =>
  useAlterar(async ({ id, arquivo, nome, legenda }: { id: string; arquivo: Blob; nome: string; legenda?: string }) => {
    const form = new FormData();
    if (legenda) form.append("legenda", legenda);
    form.append("arquivo", arquivo, nome);
    const r = await fetch(`/api/conversas/${id}/anexos`, { method: "POST", body: form, credentials: "same-origin" });
    if (!r.ok) throw new ErroRequisicao(r.status, (await r.json().catch(() => null)) ?? { erro: "rede", mensagem: "Falha no envio" });
    return r.json() as Promise<{ id: string }>;
  }, "conversas");

export const useAtribuir = () =>
  useAlterar(({ id, usuarioId }: { id: string; usuarioId: string | null }) => api(`/conversas/${id}/atribuir`, { method: "POST", json: { usuarioId } }), "conversas");
export const useStatusConversa = () =>
  useAlterar(({ id, status }: { id: string; status: StatusConversa }) => api(`/conversas/${id}/status`, { method: "POST", json: { status } }), "conversas");
export const useVincularPaciente = () =>
  useAlterar(({ id, pacienteId }: { id: string; pacienteId: string | null }) => api(`/conversas/${id}/paciente`, { method: "POST", json: { pacienteId } }), "conversas");
export const useIniciarConversa = () =>
  useAlterar((d: { pacienteId: string; canalId: string }) => api<{ id: string }>("/conversas/iniciar", { method: "POST", json: d }), "conversas");
/**
 * Abre (ou cria) a conversa do paciente pelo número da unidade e vai para ela.
 * Com `resposta`, a resposta rápida desse atalho já entra escrita, com as variáveis trocadas.
 */
export function useMensagemAoPaciente() {
  const cliente = useQueryClient();
  const iniciar = useIniciarConversa();
  const navegar = useNavigate();
  const abrir = async ({ pacienteId, unidadeId, resposta }: { pacienteId: string; unidadeId?: string; resposta?: string }) => {
    const ativos = (await cliente.fetchQuery(consultaCanais)).filter((c) => c.ativo);
    // Prefere um número conectado, e da unidade do horário quando houver.
    const nota = (c: Canal) => (c.status === "conectado" ? 2 : 0) + (c.unidadeId === unidadeId ? 1 : 0);
    const canal = [...ativos].sort((x, y) => nota(y) - nota(x))[0];
    if (!canal) throw new ErroRequisicao(404, { erro: "sem_canal", mensagem: "Nenhum número de WhatsApp ativo. Cadastre em WhatsApp e horário." });
    const { id } = await iniciar.mutateAsync({ pacienteId, canalId: canal.id });
    await navegar({ to: "/conversas", search: { c: id, resposta } });
  };
  return { abrir, pendente: iniciar.isPending };
}

export const useSimular = () =>
  useAlterar((d: { canalId: string; telefone: string; nome?: string; texto: string }) => api<{ conversaId: string }>("/whatsapp/simular", { method: "POST", json: d }), "conversas");

export const useSalvarResposta = () =>
  useAlterar(
    ({ id, dados }: { id?: string; dados: { atalho: string; texto: string } }) =>
      api<RespostaRapida>(id ? `/conversas-respostas/${id}` : "/conversas-respostas", { method: id ? "PUT" : "POST", json: dados }),
    "conversas",
  );
export const useRemoverResposta = () => useAlterar((id: string) => api(`/conversas-respostas/${id}`, { method: "DELETE" }), "conversas");

/** Telefone com DDI para exibição: +55 (94) 99111-0001. */
export function formatarNumero(digitos: string): string {
  const d = digitos.replace(/\D/g, "");
  const local = d.startsWith("55") && d.length >= 12 ? d.slice(2) : d;
  const m = local.match(/^(\d{2})(\d{4,5})(\d{4})$/);
  return m ? `+55 (${m[1]}) ${m[2]}-${m[3]}` : `+${d}`;
}
